import { clsx } from '@alexis/helpers/clsx';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import CaretDownIcon from '@icons/new/caret-round-down.svg';
import CaretUpIcon from '@icons/new/caret-round-up.svg';

import { getIsRtlState } from '@redux/selectors';

import { SortOrder } from '@wegoTypes/sortOrder';

import styles from './SortButton.module.scss';

interface SortButtonProps {
  isActive?: boolean;
  disabled?: boolean;
  label?: string;
  offSetLeftId?: string;
  defaultSortOrder?: SortOrder;
  tooltip?: string;
  isLast?: boolean;
  onClick?: () => void;
}

const SortButton = ({
  isActive,
  disabled,
  label,
  offSetLeftId,
  tooltip,
  isLast,
  defaultSortOrder = 'asc',
  onClick,
}: SortButtonProps) => {
  const isRtl = useSelector(getIsRtlState);

  const { sortOrder } = useFlightSearchSort();

  const referenceElement = document.getElementById(offSetLeftId || '');
  const parentElement = referenceElement?.offsetParent;

  let offSet: number | undefined;

  if (referenceElement && parentElement) {
    const offSetRight =
      parentElement.clientWidth - (referenceElement.offsetLeft + referenceElement.offsetWidth);
    offSet = isRtl ? offSetRight : referenceElement.offsetLeft;
  }

  const icons = useMemo(() => {
    return [
      {
        icon: CaretUpIcon,
        iconActive: sortOrder !== defaultSortOrder,
      },
      {
        icon: CaretDownIcon,
        iconActive: sortOrder === defaultSortOrder,
      },
    ];
  }, [defaultSortOrder, sortOrder]);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }

    onClick?.();
  }, [disabled, onClick]);

  const Content = useMemo(() => {
    return (
      <div
        data-testid='sort-button'
        className={clsx(
          styles.container,
          isActive && styles.active,
          disabled && styles.disabled,
          isLast && styles.last,
        )}
        onClick={handleClick}
        style={
          offSet !== undefined
            ? {
                left: isRtl ? 'unset' : offSet - 16,
                position: 'absolute',
                right: isRtl ? offSet : 'unset',
              }
            : undefined
        }
      >
        <div>{label}</div>

        <div className={styles.icons}>
          {icons.map(({ icon: Icon, iconActive }, index) => (
            <Icon
              key={index}
              role='img'
              className={clsx(styles.icon, isActive && iconActive && styles.activeIcon)}
            />
          ))}
        </div>
      </div>
    );
  }, [disabled, handleClick, icons, isActive, isLast, isRtl, label, offSet]);

  if (tooltip) {
    return (
      <div className={styles.tooltipContainer}>
        <div className={clsx(styles.tooltip, styles.top)}>{tooltip}</div>

        {Content}
      </div>
    );
  }

  return Content;
};

export default SortButton;
